import React, { PureComponent } from 'react';
import LineMeasurement from './LineMeasurement';
import { EditorState } from 'draft-js';
import './MeasurementLayerBase.css';

export default class MeasurementLayerBase extends PureComponent {
  createdId = null;

  componentDidMount() {
    this.root.addEventListener('mousedown', this.onMouseDown);
    this.root.addEventListener('click', this.onClick);
    document.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('blur', this.endDrag);
  }

  componentWillUnmount() {
    this.root.removeEventListener('mousedown', this.onMouseDown);
    this.root.removeEventListener('click', this.onClick);
    document.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('blur', this.endDrag);
  }

  render() {
    const className = 'measurement-layer-base' + (this.props.mode ? ' any-mode-on' : '');
    return (
      <div
        className={className}
        style={{ height: this.props.heightInPx, width: this.props.widthInPx }}
        ref={(e) => (this.root = e)}>
        {this.props.measurements?.map(this.createMeasurementComponent)}
      </div>
    );
  }

  createMeasurementComponent = (measurement) => {
    if (measurement.type === 'line') {
      return (
        <LineMeasurement
          key={measurement.id}
          line={JSON.parse(JSON.stringify(measurement))}
          parentWidth={this.props.widthInPx}
          parentHeight={this.props.heightInPx}
          measureLine={this.props.measureLine}
          onChange={this.onChange}
          onCommit={this.props.onCommit}
          onDeleteButtonClick={this.delete}
          changeTextVal={this.props.changeTextVal}
          disablePan={this.props.disablePan}
          mode={this.props.mode}
          isMeasureToolEditable={this.props.isMeasureToolEditable}
          updateSelection={this.props.updateSelection}
          clickedOutsideAllLines={this.props.clickedOutsideAllLines}
          isShiftPressed={this.props.isShiftPressed}
          scale={this.props.scale}
          measurementUnit={this.props.measurementUnit}
        />
      );
    } else {
      return false;
    }
  };

  onMouseDown = (event) => {
    this.finishAnyTextEdit();
    if (event.button === 0) {
      if (this.props.mode === 'line') {
        event.preventDefault();
        this.lineCreationInProgress = true;
        this.mouseXAtPress = event.clientX;
        this.mouseYAtPress = event.clientY;
      }
    }
  };

  onMouseMove = (event) => {
    if (this.lineCreationInProgress) {
      const rect = this.root.getBoundingClientRect();
      const endX = this.clamp((event.clientX - rect.left) / this.props.widthInPx);
      const endY = this.clamp((event.clientY - rect.top) / this.props.heightInPx);
      if (this.createdId === null) {
        this.createdId = this.getNextId();
        const startX = this.clamp((this.mouseXAtPress - rect.left) / this.props.widthInPx);
        const startY = this.clamp((this.mouseYAtPress - rect.top) / this.props.heightInPx);
        const line = {
          id: this.createdId,
          type: 'line',
          startX,
          startY,
          endX,
          endY,
          styleApplied: this.props.styleApplied,
        };
        this.root.classList.add('line-end-dragged');
        this.props.onChange([...this.props.measurements, line]);
      } else {
        const line = this.props.measurements.filter((a) => a.id === this.createdId)[0];
        this.onChange({ ...line, endX, endY });
      }
    }
  };

  onMouseUp = (event) => this.endDrag();

  endDrag = () => {
    if (this.lineCreationInProgress) {
      this.lineCreationInProgress = false;
      if (this.createdId !== null) {
        this.root.classList.remove('line-end-dragged');
      }
    } else if (this.circleCreationInProgress) {
      this.circleCreationInProgress = false;
      if (this.createdId !== null) {
        this.root.classList.remove('circle-stroke-dragged');
      }
    }
    if (this.createdId !== null) {
      this.props.onCommit(this.props.measurements.filter((a) => a.id === this.createdId)[0]);
      this.createdId = null;
    }
  };

  onClick = (event) => {
    if (this.props.mode === 'text') {
      const id = this.getNextId();
      const rect = this.root.getBoundingClientRect();
      const arrowX = (event.clientX - rect.left) / this.props.widthInPx;
      const arrowY = (event.clientY - rect.top) / this.props.heightInPx;
      const xOffsetDirection = arrowX < 0.8 ? 1 : -1;
      const yOffsetDirection = arrowY < 0.8 ? 1 : -1;
      const textX = arrowX + xOffsetDirection * 0.05;
      const textY = arrowY + yOffsetDirection * 0.07;
      const text = {
        id,
        type: 'text',
        arrowX,
        arrowY,
        textX,
        textY,
        editorState: null,
        editable: true,
      };
      this.props.onChange([...this.props.measurements, text]);
      this.props.onCommit(text);
    }
  };

  getNextId = () =>
    this.props.measurements.length > 0
      ? Math.max(...this.props.measurements?.map((a) => a.id)) + 1
      : 0;

  onChange = (m) =>
    this.props.onChange(this.props.measurements?.map((n) => (m.id === n.id ? m : n)));

  delete = (m) => {
    this.props.onChange(this.props.measurements.filter((n) => n.id !== m.id));
    this.props.onCommit(m, 'delete');
  };

  clamp = (value) => Math.min(1, Math.max(0, value));

  finishAnyTextEdit = () => {
    const editable = this.props.measurements.filter((m) => m.type === 'text' && m.editable)[0];
    if (editable) {
      this.props.onChange(
        this.props.measurements?.map((m) => (m === editable ? this.finishEdit(m) : m))
      );
    }
  };

  finishEdit = (text) => ({
    ...text,
    editorState: EditorState.moveFocusToEnd(EditorState.moveSelectionToEnd(text.editorState)),
    editable: false,
  });
}
